'use client';

import { resetFormStartEvents } from '@/components/helpers/analytics/gtm';
import { ILocationData } from '@/components/helpers/fetchInitialProps';
import {
  IOrNavBarBlockProps,
  TFooterProps,
  TPage,
  TBannerProps,
  ICustomUrls,
  IDesignStudiosJSON,
} from '@/lib/contentstack-sdk/types';
import { IStudioHit } from '@/lib/search/types';
import { usePathname } from 'next/navigation';
import { createContext, ReactNode, useContext, useEffect } from 'react';
import { FlagConfiguration } from '../lib/feature-flags/types';
import {
  InMemoryProvider,
  OpenFeature,
  OpenFeatureProvider,
} from '@openfeature/react-sdk';

type TInitialProps = {
  header: IOrNavBarBlockProps;
  footer: TFooterProps;
  entries: TPage[];
  banner: TBannerProps;
  locationData: ILocationData[];
  studioData: IStudioHit[];
  globalLinks: ICustomUrls[];
  studio3DData: IDesignStudiosJSON;
};

export const InitialPropsContext = createContext<TInitialProps | null>(null);

export const RootLayoutClient = ({
  children,
  initialProps,
  flags,
  debug,
}: {
  children: ReactNode;
  initialProps: TInitialProps;
  flags: FlagConfiguration;
  debug: string | undefined;
}) => {
  const pathname = usePathname();

  // Support client side DEBUG flags
  try {
    if (typeof window !== 'undefined') {
      // Instantiate and set our provider (be sure this only happens once)!
      // Note: there's no need to await its initialization, the React SDK handles re-rendering and suspense for you!
      OpenFeature.setProvider(new InMemoryProvider(flags));

      if (typeof localStorage !== 'undefined') {
        if (debug) {
          localStorage.setItem('debug', debug);
        } else {
          localStorage.removeItem('debug');
        }
      }
    }
  } catch (ex) {
    console.error('Failed to set debug environment', ex);
  }

  useEffect(() => {
    // On path change, reset all forms
    resetFormStartEvents();
  }, [pathname]);

  return (
    <InitialPropsContext.Provider value={initialProps}>
      <OpenFeatureProvider>
        <main>{children}</main>
      </OpenFeatureProvider>
    </InitialPropsContext.Provider>
  );
};

// Hook to use the initial props in client components
export function useInitialProps() {
  const context = useContext(InitialPropsContext);
  if (!context) {
    throw new Error('useInitialProps must be used within RootLayoutClient');
  }
  return context;
}
